import { Alert, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import {
  SLA_ESCALATION_MAX_LEVELS,
  SLA_ESCALATION_MIN_LEVELS,
  isSlaEscalationRuntimeEnabled,
} from "model/slaModel";
import type { SlaSettingsDto } from "model/slaModel";
import {
  SLA_EDITOR_NO_ACTOR,
  SLA_EDITOR_TEXT_KEYS,
  clampSlaEscalationLevels,
  isSlaEscalationInert,
  type SlaEditorActorOption,
  type SlaTargetDraft,
} from "model/slaEditorModel";

/**
 * Escalation opzionale per target (piano Client sez. 5 F2a e sez. 11.1).
 *
 * Campi: ritardo in **ore lavorative** (`escalation_after_working_hours`, NULL =
 * nessuna escalation), livelli 1..5 (`escalation_levels`) e ruolo del livello
 * successivo (`escalation_role_id`, NULL = ricaduta sul responsabile).
 *
 * Comunicazione richiesta da F2a e implementata qui:
 * - la policy e' congelata all'avvio dell'istanza: modificare l'escalation a
 *   design-time **non** escalationa le istanze gia' avviate (sez. 11.1);
 * - l'escalation per target resta **inerte** finche' l'organizzazione non ha
 *   `enabled` e `escalation_enabled` entrambi attivi (doppio gate del worker,
 *   `Job.SLA.Worker.pas`): senza le settings il secondo avviso non e' mostrabile,
 *   quindi e' mostrato solo quando il chiamante le fornisce.
 *
 * ── Decisione 11 (2026-09-19) = opzione B: avviso non bloccante ─────────────
 * `plans/SLA_Client_Plan.md` riga 215, sez. 11.5 punto 4 e sez. 13: con
 * `escalation_after_working_hours` NULL ma `escalation_levels > 1` o
 * `escalation_role_id` valorizzati, la configurazione e' **inerte** (il gate del
 * worker pretende il ritardo non nullo). Scelta approvata: i campi **restano
 * abilitati** e il salvataggio **non** viene bloccato; l'utente vede l'avviso
 * inline (`escalationInertWarning`) quando `isSlaEscalationInert(draft)` e' true.
 * L'avviso e' aggiuntivo, non sostituisce quello del gate di organizzazione.
 * ───────────────────────────────────────────────────────────────────────────
 */
interface SlaEscalationFieldsProps {
  draft: SlaTargetDraft;
  onChange: (patch: Partial<SlaTargetDraft>) => void;
  roles: SlaEditorActorOption[];
  /** Gate di organizzazione: `null`/`undefined` = settings non disponibili. */
  settings?: Pick<SlaSettingsDto, "enabled" | "escalationenabled"> | null;
  disabled?: boolean;
  idPrefix: string;
  /** true quando il chiamante rende gia' l'intestazione della sezione. */
  hideTitle?: boolean;
}

/**
 * Campo "livelli di escalation" (1..5) nascosto nella UI per ora (2026-10-14).
 * Solo resa: stato, validazione e payload restano invariati, quindi il PUT
 * continua a inviare `escalation_levels` con il valore della bozza (quello
 * letto dal Server o `SLA_ESCALATION_MIN_LEVELS` per un target nuovo). Per
 * riabilitarlo basta rimettere `true`.
 */
const SLA_ESCALATION_LEVELS_FIELD_VISIBLE = false;

const ESCALATION_LEVELS: number[] = Array.from(
  { length: SLA_ESCALATION_MAX_LEVELS - SLA_ESCALATION_MIN_LEVELS + 1 },
  (_, index) => SLA_ESCALATION_MIN_LEVELS + index,
);

const COMPACT_ALERT_SX = { py: 0, "& .MuiAlert-message": { fontSize: 13 } };

const SlaEscalationFields = ({
  draft,
  onChange,
  roles,
  settings = null,
  disabled = false,
  idPrefix,
  hideTitle = false,
}: SlaEscalationFieldsProps) => {
  const translations = useTranslations();
  /** Decisione 11 (sez. 13): avviso, non disabilitazione dei campi. */
  const escalationInert = isSlaEscalationInert(draft);

  return (
    <Stack spacing={1.5}>
      {!hideTitle && (
        <Typography variant="subtitle2">
          {getTranslation(translations, SLA_EDITOR_TEXT_KEYS.escalationTitle)}
        </Typography>
      )}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "flex-start" }}>
        <TextField
          id={`${idPrefix}-escalation-hours`}
          size="small"
          type="number"
          fullWidth
          disabled={disabled}
          label={getTranslation(
            translations,
            SLA_EDITOR_TEXT_KEYS.escalationAfterWorkingHours,
          )}
          inputProps={{ min: 0, step: "any" }}
          value={draft.escalationAfterWorkingHours ?? ""}
          helperText={getTranslation(
            translations,
            SLA_EDITOR_TEXT_KEYS.escalationNoDelayHint,
          )}
          onChange={(event) => {
            const parsed = event.target.value === "" ? null : Number(event.target.value);
            onChange({
              escalationAfterWorkingHours:
                parsed === null || !Number.isFinite(parsed) ? null : parsed,
            });
          }}
        />
        {SLA_ESCALATION_LEVELS_FIELD_VISIBLE && (
          <TextField
            id={`${idPrefix}-escalation-levels`}
            select
            size="small"
            fullWidth
            disabled={disabled}
            label={getTranslation(translations, SLA_EDITOR_TEXT_KEYS.escalationLevels)}
            value={draft.escalationLevels}
            onChange={(event) =>
              onChange({
                escalationLevels: clampSlaEscalationLevels(Number(event.target.value)),
              })
            }
          >
            {ESCALATION_LEVELS.map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
      <TextField
        id={`${idPrefix}-escalation-role`}
        select
        size="small"
        fullWidth
        disabled={disabled}
        label={getTranslation(translations, SLA_EDITOR_TEXT_KEYS.escalationRole)}
        value={draft.escalationRoleId ?? SLA_EDITOR_NO_ACTOR}
        onChange={(event) =>
          onChange({
            escalationRoleId:
              event.target.value === SLA_EDITOR_NO_ACTOR
                ? null
                : Number(event.target.value),
          })
        }
      >
        <MenuItem value={SLA_EDITOR_NO_ACTOR}>
          {getTranslation(translations, SLA_EDITOR_TEXT_KEYS.escalationRoleFallback)}
        </MenuItem>
        {roles.map((role) => (
          <MenuItem key={role.id} value={role.id}>
            {role.name}
          </MenuItem>
        ))}
      </TextField>
      {escalationInert && (
        <Alert severity="warning" sx={COMPACT_ALERT_SX}>
          {getTranslation(
            translations,
            SLA_EDITOR_TEXT_KEYS.escalationInertWarning,
          )}
        </Alert>
      )}
      <Alert severity="info" sx={COMPACT_ALERT_SX}>
        {getTranslation(translations, SLA_EDITOR_TEXT_KEYS.escalationFrozenPolicy)}
      </Alert>
      {settings && !isSlaEscalationRuntimeEnabled(settings) && (
        <Alert severity="warning" sx={COMPACT_ALERT_SX}>
          {getTranslation(
            translations,
            SLA_EDITOR_TEXT_KEYS.escalationOrganizationGate,
          )}
        </Alert>
      )}
    </Stack>
  );
};

export default SlaEscalationFields;
